import type { EventBus } from './eventBus';
import type { Vector2 } from './constants';
import { WA_PHYSICS } from './constants';

export interface TargetingState {
  active: boolean;
  cursorPos: Vector2;
  lockedTarget: Vector2 | null;
  withDpad: boolean;
}

export interface TargetingCallbacks {
  onCursorVisibilityChanged: (visible: boolean) => void;
  onCursorPositionChanged: (pos: Vector2) => void;
  onLockedTargetChanged: (target: Vector2 | null) => void;
  playLockSound: () => void;
}

export class TargetingSystem {
  private active = false;
  private withDpad = false;
  private cursorPos: Vector2 = { x: 0, y: 0 };
  private lockedTarget: Vector2 | null = null;
  private eventBus: EventBus | null = null;
  private callbacks: TargetingCallbacks | null = null;

  private currentWormId: string | null = null;

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  setCallbacks(callbacks: TargetingCallbacks): void {
    this.callbacks = callbacks;
  }

  enter(initialPos: Vector2, weaponId: string, wormId: string): void {
    if (this.active) return;

    this.active = true;
    this.withDpad = false;
    this.cursorPos = { x: initialPos.x, y: initialPos.y };
    this.currentWormId = wormId;

    this.callbacks?.onCursorVisibilityChanged(true);
    this.callbacks?.onCursorPositionChanged(this.cursorPos);

    this.eventBus?.emit('targeting:started', {
      weaponId,
      wormId,
    });
  }

  exit(): void {
    if (!this.active) return;

    this.active = false;
    this.withDpad = false;

    this.callbacks?.onCursorVisibilityChanged(false);

    if (this.currentWormId) {
      this.eventBus?.emit('targeting:cancelled', {
        wormId: this.currentWormId,
      });
    }

    this.currentWormId = null;
  }

  lockTarget(pos: Vector2): void {
    this.lockedTarget = { x: pos.x, y: pos.y };

    this.callbacks?.onLockedTargetChanged(this.lockedTarget);
    this.callbacks?.playLockSound();

    if (this.currentWormId) {
      this.eventBus?.emit('targeting:locked', {
        position: this.lockedTarget,
        wormId: this.currentWormId,
      });
    }

    this.exit();
  }

  unlockTarget(): void {
    this.lockedTarget = null;
    this.callbacks?.onLockedTargetChanged(null);
  }

  setCursorPosition(pos: Vector2): void {
    this.cursorPos.x = pos.x;
    this.cursorPos.y = pos.y;
    this.clampCursorPosition();
    this.callbacks?.onCursorPositionChanged(this.cursorPos);
  }

  moveCursor(dx: number, dy: number): void {
    this.cursorPos.x += dx;
    this.cursorPos.y += dy;
    this.clampCursorPosition();
    this.callbacks?.onCursorPositionChanged(this.cursorPos);
  }

  private clampCursorPosition(): void {
    this.cursorPos.x = Math.max(0, Math.min(WA_PHYSICS.WORLD.WIDTH, this.cursorPos.x));
    this.cursorPos.y = Math.max(0, Math.min(WA_PHYSICS.WORLD.HEIGHT, this.cursorPos.y));
  }

  setWithDpad(value: boolean): void {
    this.withDpad = value;
  }

  isActive(): boolean {
    return this.active;
  }

  isWithDpad(): boolean {
    return this.withDpad;
  }

  getCursorPosition(): Vector2 {
    return { x: this.cursorPos.x, y: this.cursorPos.y };
  }

  getLockedTarget(): Vector2 | null {
    return this.lockedTarget ? { x: this.lockedTarget.x, y: this.lockedTarget.y } : null;
  }

  hasLockedTarget(): boolean {
    return this.lockedTarget !== null;
  }

  getState(): TargetingState {
    return {
      active: this.active,
      cursorPos: { x: this.cursorPos.x, y: this.cursorPos.y },
      lockedTarget: this.lockedTarget ? { x: this.lockedTarget.x, y: this.lockedTarget.y } : null,
      withDpad: this.withDpad,
    };
  }
}
